import {
    CartonProduct,
    OfferDraft,
    OfferPhotoUpload,
    OfferProperty,
} from '../../../../types';

export type OfferDraftFormValues = {
    product_id: string;
    price: string;
    amount: string;
    property_set: Record<string, string>;
    photos: OfferPhotoUpload[];
};

export type OfferDraftField = keyof OfferDraftFormValues;
export type OfferDraftErrors = Partial<
    Record<OfferDraftField, string | string[]>
>;

export const EMPTY_OFFER_DRAFT_VALUES: OfferDraftFormValues = {
    product_id: '',
    price: '1',
    amount: '1',
    property_set: {},
    photos: [],
};

export const BULK_OFFER_FIELD_ERRORS: Record<OfferDraftField, string> = {
    product_id: 'Оберіть товар',
    price: 'Ціна повинна бути цілим числом більше нуля',
    amount: 'Кількість повинна бути більше нуля',
    property_set: 'Не може бути порожнім',
    photos: 'Додайте хоча б одне фото',
};

export const MAX_STRING_PROPERTY_LENGTH = 120;
export const MAX_OFFER_PRICE = 1000000;
export const MAX_OFFER_DRAFTS_COUNT = 100;
export const STRING_PROPERTY_LENGTH_ERROR = `Максимум ${MAX_STRING_PROPERTY_LENGTH} символів`;
export const UNSUPPORTED_PROPERTY_VALUE_ERROR =
    'Обране значення недоступне для цього товару';

export const createOfferDraftId = () => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const resolveOfferPropertiesForProduct = (
    product: CartonProduct | null,
    offerProperties: OfferProperty[],
) => {
    if (!product) return [];

    const availableLanguages = new Set(
        (product.available_languages ?? []).map((language) =>
            language.toLowerCase(),
        ),
    );

    return offerProperties
        .filter(
            (property) => property.product_type_id === product.product_type_id,
        )
        .map((property) => {
            if (property.slug !== 'language' || availableLanguages.size === 0) {
                return property;
            }

            return {
                ...property,
                offer_property_options: property.offer_property_options.filter(
                    (option) =>
                        availableLanguages.has(option.slug.toLowerCase()),
                ),
            };
        });
};

export const buildDefaultPropertySet = (
    product: CartonProduct | null,
    offerProperties: OfferProperty[],
): Record<string, string> =>
    resolveOfferPropertiesForProduct(product, offerProperties).reduce<
        Record<string, string>
    >((defaultPropertySet, property) => {
        const defaultOption = property.offer_property_options.find(
            (option) => option.default,
        );
        const selectedDefaultOption =
            defaultOption ??
            (property.slug === 'language'
                ? property.offer_property_options[0]
                : undefined);

        if (selectedDefaultOption) {
            defaultPropertySet[property.slug] = selectedDefaultOption.slug;
        }

        return defaultPropertySet;
    }, {});

export const mergePropertySetWithDefaults = (
    product: CartonProduct | null,
    propertySet: Record<string, string> | undefined,
    offerProperties: OfferProperty[],
): Record<string, string> => ({
    ...buildDefaultPropertySet(product, offerProperties),
    ...(propertySet ?? {}),
});

export const buildOfferDraftFormValues = (
    draft: Pick<
        OfferDraft,
        | 'product_id'
        | 'price'
        | 'amount'
        | 'property_set'
        | 'product'
        | 'photos'
    >,
    offerProperties: OfferProperty[],
): OfferDraftFormValues => ({
    product_id: draft.product_id,
    price: draft.price,
    amount: draft.amount,
    property_set: mergePropertySetWithDefaults(
        draft.product ?? null,
        draft.property_set,
        offerProperties,
    ),
    photos: [...draft.photos],
});

export const normalizeOfferDraft = (draft: Partial<OfferDraft>): OfferDraft => {
    const product = draft.product ?? null;
    const productId =
        draft.product_id?.toString() ?? product?.id?.toString() ?? '';

    return {
        draft_id: draft.draft_id || createOfferDraftId(),
        product_id: productId,
        price: draft.price?.toString() ?? EMPTY_OFFER_DRAFT_VALUES.price,
        amount: draft.amount?.toString() ?? EMPTY_OFFER_DRAFT_VALUES.amount,
        property_set: { ...(draft.property_set ?? {}) },
        photo_blob_ids: draft.photo_blob_ids ?? [],
        photos:
            draft.photos ??
            (draft.photo_blob_ids ?? []).map((signedId) => ({
                signed_id: signedId,
                filename: 'Фото пропозиції',
                preview_url: '',
            })),
        product,
        universe_id: draft.universe_id ?? product?.universe_id ?? null,
        product_type_id:
            draft.product_type_id ?? product?.product_type_id ?? null,
        source: draft.source ?? null,
        source_code: draft.source_code ?? null,
        import_name: draft.import_name ?? null,
        import_error: draft.import_error ?? null,
        scryfall_id: draft.scryfall_id ?? null,
        auto_price_status: draft.auto_price_status ?? null,
        auto_price_error: draft.auto_price_error ?? null,
        auto_price_source: draft.auto_price_source ?? null,
        auto_price_base: draft.auto_price_base ?? null,
    };
};

export const DEFAULT_AUTO_PRICE_MARKUP = 0;
export const MAX_AUTO_PRICE_MARKUP = MAX_OFFER_PRICE;
export const MAX_AUTO_PRICE_PERCENT_ADJUSTMENT = 1_000;
export const MIN_AUTO_PRICE_PERCENT_ADJUSTMENT = -100;

export type AutoPriceAdjustmentMode = 'fixed' | 'percent';

export const applyAutoPriceAdjustment = (
    price: string,
    adjustment: number,
    mode: AutoPriceAdjustmentMode,
) => {
    const parsedPrice = Number.parseFloat(price);

    if (!Number.isFinite(parsedPrice)) return price;

    const adjustedPrice =
        mode === 'percent'
            ? parsedPrice * (1 + adjustment / 100)
            : parsedPrice + adjustment;

    return Math.min(
        MAX_OFFER_PRICE,
        Math.max(1, Math.round(adjustedPrice)),
    ).toString();
};

export const hydrateOfferDraftDefaults = (
    draft: OfferDraft,
    offerProperties: OfferProperty[],
): OfferDraft => ({
    ...draft,
    property_set: mergePropertySetWithDefaults(
        draft.product,
        draft.property_set,
        offerProperties,
    ),
});

export const serializeOfferDraft = (draft: OfferDraft) => ({
    draft_id: draft.draft_id,
    product_id: draft.product_id,
    price: draft.price,
    amount: draft.amount,
    property_set: { ...draft.property_set },
    photo_blob_ids: draft.photos.map((photo) => photo.signed_id),
    universe_id: draft.universe_id ?? null,
    product_type_id: draft.product_type_id ?? null,
    source: draft.source ?? null,
    source_code: draft.source_code ?? null,
    import_name: draft.import_name ?? null,
    import_error: draft.import_error ?? null,
    scryfall_id: draft.scryfall_id ?? null,
    auto_price_status: draft.auto_price_status ?? null,
    auto_price_error: draft.auto_price_error ?? null,
    auto_price_source: draft.auto_price_source ?? null,
    auto_price_base: draft.auto_price_base ?? null,
});

export const validateOfferDraft = (
    draft: OfferDraft,
    offerProperties: OfferProperty[],
): OfferDraftErrors => {
    const nextErrors: OfferDraftErrors = {};
    const trimmedPrice = draft.price.trim();
    const parsedPrice = Number(trimmedPrice);
    const parsedAmount = Number.parseInt(draft.amount, 10);
    const properties = resolveOfferPropertiesForProduct(
        draft.product,
        offerProperties,
    );

    if (!draft.product || !draft.product_id) {
        nextErrors.product_id = BULK_OFFER_FIELD_ERRORS.product_id;
    }

    if (draft.product?.offer_photos_required && draft.photos.length === 0) {
        nextErrors.photos = BULK_OFFER_FIELD_ERRORS.photos;
    }

    if (
        !/^\d+$/.test(trimmedPrice) ||
        !Number.isFinite(parsedPrice) ||
        parsedPrice <= 0 ||
        parsedPrice > MAX_OFFER_PRICE
    ) {
        nextErrors.price = BULK_OFFER_FIELD_ERRORS.price;
    }

    if (!Number.isInteger(parsedAmount) || parsedAmount <= 0) {
        nextErrors.amount = BULK_OFFER_FIELD_ERRORS.amount;
    }

    const hasMissingRequiredProperty = properties.some((property) => {
        if (!property.required) return false;

        const value = draft.property_set[property.slug];
        if (typeof value !== 'string') {
            return !value;
        }

        return value.trim() === '';
    });

    const hasTooLongStringProperty = properties.some((property) => {
        if (property.kind !== 'string') return false;

        const value = draft.property_set[property.slug];
        return (
            typeof value === 'string' &&
            value.length > MAX_STRING_PROPERTY_LENGTH
        );
    });

    const hasUnsupportedCollectionValue = properties.some((property) => {
        if (property.kind !== 'collection') return false;

        const value = draft.property_set[property.slug];
        if (!value) return false;

        return !property.offer_property_options.some(
            (option) => option.slug === value,
        );
    });

    if (hasMissingRequiredProperty) {
        nextErrors.property_set = BULK_OFFER_FIELD_ERRORS.property_set;
    } else if (hasTooLongStringProperty) {
        nextErrors.property_set = STRING_PROPERTY_LENGTH_ERROR;
    } else if (hasUnsupportedCollectionValue) {
        nextErrors.property_set = UNSUPPORTED_PROPERTY_VALUE_ERROR;
    }

    return nextErrors;
};

export const isOfferDraftValid = (
    draft: OfferDraft,
    offerProperties: OfferProperty[],
) => Object.keys(validateOfferDraft(draft, offerProperties)).length === 0;

export const firstOfferDraftErrorMessage = (errors: OfferDraftErrors) => {
    const [firstError] = Object.values(errors);

    if (!firstError) return null;

    return Array.isArray(firstError) ? firstError[0] : firstError;
};
